import { useEffect, useMemo, useState } from "react";
import { TreeItem, SimpleTreeView } from "@mui/x-tree-view";
import { withTranslation } from "react-i18next";
import { Box, Checkbox, FormControlLabel, Popover } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { isArray, isEqual, isNull, isUndefined, size, uniq } from "lodash";
import customerHrchyService from "@plannel/services/data/customer-hrchy-service";
import debug from "@plannel/utils/debug-util";
import itemHrchyService from "@plannel/services/data/item-hrchy-service";
import GroupedTokenSelect from "@plannel/components/ui/GroupedTokenSelect";
import { FS } from "@plannel/utils/font-util";

const TreeMultiSelect = (props) => {
  const { open, setOpen, anchorEl, setAnchorEl, level, isCust, hrchys, selectedHrchys, singleSelect = false, isIn, isLeaf = false } = props;

  const [datas, setDatas] = useState([]);
  const [data, setData] = useState([]);
  const [parentMap, setParentMap] = useState({});
  const [selected, setSelected] = useState([]);
  const [expandedList, setExpandedList] = useState([]);

  const sortData = (array, bySort) => {
    array.sort((a, b) => {
      const aValue = a[bySort];
      const bValue = b[bySort];
      if (aValue < bValue) {
        return -1;
      }
      if (aValue > bValue) {
        return 1;
      }
      return 0;
    });
    return array;
  }

  const deletePar = (node) => {
    if (!isUndefined(node)) {
      delete node.parent;
      if (node.children) {
        for (const childNode of node.children) {
          deletePar(childNode);
        }
        return node;
      }
    }
    return null;
  }

  const treeDataRender = (hrchyDatas) => {
    const treeArry = [];
    const treeObj = {};

    for (let i = 0; i < hrchyDatas.length; i++) {
      if (hrchyDatas[i].length > 0) {
        const sortedDatas = sortData(hrchyDatas[i], "hrchyLvSeqNum");
        for (const sortedData of sortedDatas) {
          const parentId = isArray(sortedData.parentCd) ? sortedData.parentCd[0] : sortedData.parentCd;
          treeObj[sortedData.hrchyCd] = {
            id: sortedData.hrchyCd,
            name: sortedData.name,
            parent: parentId
          };
          treeObj[sortedData.hrchyCd].children = [];
        }
      }
    }

    for (const nodeId in treeObj) {
      if (Object.prototype.hasOwnProperty.call(treeObj, nodeId)) {
        const treeNode = treeObj[nodeId];
        if (treeNode.parent) {
          treeObj[treeNode.parent]?.children.push(treeNode);
        } else {
          treeArry.push(treeNode);
        }
      }
    }
    const renderedData = deletePar(treeArry[0]) ? deletePar(treeArry[0]) : {};
    return renderedData;
  }

  const setFilterData = (dataResults) => {
    const lvSeqNum = level?.seqNum ?? 99;
    const filterdDatas = dataResults.filter(result => result.hrchyLvSeqNum <= lvSeqNum);
    const defaultList =
      filterdDatas.reduce((prev, curr) => {
        prev.push(curr.hrchyCd);
        return prev;
      }, []);

    setExpandedList(defaultList);
    return ([filterdDatas]);
  }

  const getAllDescendants = node => {
    const allDescendants = [];

    const addChild = child => {
      allDescendants.push(child);
      if (child?.children) {
        child.children.forEach(descend => {
          addChild(descend);
        });
      }
    };

    addChild(node);
    return allDescendants;
  };


  const retrieveCustomerHrchies = () => {
    customerHrchyService.getHrchyFilter({hrchyNum: level?.hrchyNum ?? 1})
      .then(response => {
        const { results } = response.data;
        const allData = getAllDescendants(results?.[0]);
        const filteredData = setFilterData(allData)
        setData(treeDataRender(filteredData));
        setDatas(filteredData);;
      })
      .catch(e => {
        debug(e);
      })
  };

  const retrieveItemsHrchies = () => {
    itemHrchyService.getHrchyFilter({hrchyNum: level?.hrchyNum ?? 1 })
      .then(response => {
        const { results } = response.data;
        const allData = getAllDescendants(results?.[0]);
        const filteredData = setFilterData(allData)
        setData(treeDataRender(filteredData));
        setDatas(filteredData);
      })
      .catch(e => {
        debug(e);
      })
  };

  const getAllChild = (childNode, collectedNodes = []) => {
    if (childNode === null) return collectedNodes;
    collectedNodes.push(childNode.id);

    if (Array.isArray(childNode.children)) {
      for (const node of childNode.children) {
        getAllChild(node, collectedNodes);
      }
    }

    return collectedNodes;
  }

  const goThroughAllNodes = (nodes, map) => {
    map[nodes.id] = getAllChild(nodes).splice(1);

    for (const childNode of nodes.children) {
      goThroughAllNodes(childNode, map);
    }
    return map;
  }

  const getChildById = (nodes, id) => {
    const array = [];
    const path = [];

    function getNodeById(node, nodeId, parentsPath) {
      let result = null;

      if (node.id === nodeId) {
        return node;
      } else if (Array.isArray(node.children)) {
        for (const childNode of node.children) {
          result = getNodeById(childNode, nodeId, parentsPath);

          if (!isNull((result))) {
            parentsPath.push(node.id);
            return result;
          }
        }
        return result;
      }
      return result;
    }
    const nodeToToggle = getNodeById(nodes, id, path);

    return { childNodesToToggle: getAllChild(nodeToToggle, array), path };
  };

  /*
   * 고른 노드 중 **선택 레벨의 노드만** 남겨 저장한다(조상은 버린다).
   *
   * ⚠ 레벨 비교 필드가 `cur.level` 로 잘못돼 있었다(2026-08-26 수정). 서버가 주는 노드에는
   *   `level` 이 없고 `hrchyLvSeqNum` 만 있다(id/name/hrchyCd/parentCd/ancestor/hrchyLv/
   *   hrchyLvSeqNum/leafFlg/descendant/children/topFlg). 그래서 `isEqual(undefined, 4)` 가
   *   항상 false → **항상 빈 배열이 저장돼 계층 필터가 아예 걸리지 않았다**
   *   (모달에서 골라도 그리드 셀이 계속 "Not Filtered").
   *   같은 파일의 setFilterData 는 처음부터 `hrchyLvSeqNum` 을 쓰고 있어 이 줄만 어긋나 있었다.
   */
  const filterSelectHrchy = (array) => datas[0].reduce((pre, cur) => {
    const checkLevel = level?.seqNum ? isEqual(cur.hrchyLvSeqNum, level?.seqNum) : true;
    if (checkLevel && array.includes(cur.hrchyCd)) {
      pre.push(cur.hrchyCd);
    }
    return pre;
  }, []);

  const getOnChange = (checked, nodes) => {
    const { childNodesToToggle, path } = getChildById(data, nodes.id);
    let array = [];

    if (singleSelect) {
      array = checked ? [nodes.id] : [];
    } else {
      array = checked
        ? [...selected, ...childNodesToToggle]
        : selected
          .filter((value) => !childNodesToToggle.includes(value))
          .filter((value) => !path.includes(value));

      array = uniq(array);
    }

    if (singleSelect) {
      selectedHrchys(array);
    } else {
      array.length > 0 ? selectedHrchys(filterSelectHrchy(array)) : selectedHrchys(array);
    }

    setSelected(array);
  }

  /*
   * ── 토큰 그리드용 ──────────────────────────────────────────────────────────
   * 모달(isIn) 에서는 트리 대신 토큰을 쓴다(2026-08-26). 저장되는 건 **선택 레벨의 노드뿐**이고
   * 조상은 값이 아니라 "자식을 한 번에 켜는 편의 장치"였다 → 조상을 그룹 제목으로 내리면
   * 정보를 하나도 잃지 않으면서 Dimension Setting 계열과 같은 화면이 된다.
   * (트리는 Popover 분기에 그대로 남겨 둔다.)
   *
   * 잘라낸 트리에서 선택 레벨의 노드 = **잎**이다. 그 부모를 그룹으로 묶고,
   * 제목은 루트(전사)를 뺀 조상 경로다 — 루트는 모든 그룹에 공통이라 붙여 봐야 정보가 없다.
   */
  const buildTokenGroups = (root) => {
    if (!root?.id) {
      return [];
    }
    // 최상위 레벨만 고르는 경우 — 잘라낸 트리에 자식이 없다. 루트 하나가 곧 선택지다
    if (!(root.children?.length > 0)) {
      return [{ key: root.id, label: "", options: [{ value: root.id, label: root.name }] }];
    }

    const groups = [];
    const walk = (node, pathNames) => {
      const children = node.children ?? [];
      if (children.length === 0) {
        return;
      }
      const leafChildren = children.filter((child) => !(child.children?.length > 0));
      const branchChildren = children.filter((child) => child.children?.length > 0);

      if (leafChildren.length > 0) {
        groups.push({
          key: node.id,
          // 루트 직속 잎이면 경로가 비므로 루트 이름을 쓴다(빈 제목이면 그룹 구분이 사라진다)
          label: pathNames.length > 0 ? pathNames.join(" › ") : node.name,
          options: leafChildren.map((child) => ({ value: child.id, label: child.name })),
        });
      }
      branchChildren.forEach((child) => walk(child, [...pathNames, child.name]));
    };

    walk(root, []);
    return groups;
  };

  const tokenGroups = buildTokenGroups(data);

  // GroupedTokenSelect 가 groups 순서(=트리 순서)로 정렬해서 넘겨준다 — 저장값이 흔들리지 않는다
  const applyTokenSelection = (next) => {
    setSelected(next);
    selectedHrchys(next.length > 0 ? filterSelectHrchy(next) : next);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };


  useEffect(() => {
    if (open) {
      setExpandedList([]);
      if (isCust) {
        retrieveCustomerHrchies();
      } else {
        retrieveItemsHrchies();
      }
      if (hrchys?.length > 0) {
        setSelected(hrchys);
      } else {
        setSelected([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (size(data) > 0) {
      setParentMap(goThroughAllNodes(data, {}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const checkedNodes = useMemo(() => {
    if (size(parentMap) === 0 || size(selected) === 0) {
      return new Set(selected);
    }

    const result = new Set(selected);

    let changed = true;

    while (changed) {
      changed = false;
      for (const nodeId of Object.keys(parentMap)) {
        if (parentMap[nodeId]?.length > 0 && !result.has(nodeId)) {
          const allChildrenSelected = parentMap[nodeId].every((childId) => result.has(childId));
          if (allChildrenSelected) {
            result.add(nodeId);
            changed = true;
          }
        }
      }
    }

    return result;
  }, [selected, parentMap]);

  const renderTree = (nodes) => {
    const basicCondition = !isNull(parentMap) && parentMap[nodes?.id]?.length > 0;
    const checked = checkedNodes.has(nodes?.id);
    const indeterminate = basicCondition &&
      parentMap[nodes?.id]?.some((childNodeId) => checkedNodes.has(childNodeId)) &&
      !checked;

    return (
      <TreeItem
        key={nodes?.id}
        itemId={nodes?.id}
        sx={{ boxSizing: "border-box" }}
        label={
          /* 행 룩은 드롭다운 공통 규격과 맞춘다 — 둥근 호버, 체크된 행은 primary 틴트
             (AbcXyzFilterModal 팝오버 행과 같은 값). 체크박스 p 를 1→0.5 로 줄여 행 높이도 낮췄다. */
          <FormControlLabel
            control={
              <Checkbox
                sx={{ p: 0.5 }}
                checked={checked}
                size="small"
                indeterminate={!checked && indeterminate}
                disabled={isLeaf && basicCondition}
                onChange={(event) => getOnChange(event.currentTarget.checked, nodes)}
                onClick={(e) => e.stopPropagation()}
              />
            }
            sx={{
              m: 0, width: "100%", pr: 0.75, borderRadius: 1.5,
              bgcolor: (theme) => (checked ? alpha(theme.palette.primary.main, 0.12) : "transparent"),
              "&:hover": { bgcolor: (theme) => alpha(theme.palette.primary.main, checked ? 0.16 : 0.06) },
            }}
            label={<Box sx={{
              fontSize: FS.TITLE,
              overflow: "hidden",          // 이게 없으면 ellipsis 가 안 걸린다(그냥 넘쳐 잘렸다)
              textOverflow: "ellipsis",
              lineHeight: 1.5,
              whiteSpace: "nowrap"
            }}>{nodes?.name}</Box>}
            key={nodes?.id}
          />
        }
      >
        {Array.isArray(nodes?.children)
          ? nodes?.children.map((node) => renderTree(node))
          : null}
      </TreeItem>
    );
  }

  return (
    expandedList?.length > 0 ?
      isIn ?
        <Box id="hrchySelect">
          <GroupedTokenSelect groups={tokenGroups} value={selected} onChange={applyTokenSelection} />
        </Box>
        :
        <Popover
          id={open ? 'hrchy-popover' : undefined}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Box id="hrchySelect">
            <SimpleTreeView
              sx={{ maxHeight: "300px", paddingLeft: "10px", overflowY: "auto" }}
              defaultExpandedItems={expandedList}
            >
              {renderTree(data)}
            </SimpleTreeView>
          </Box>
        </Popover> : null
  );
}
export default withTranslation()(TreeMultiSelect);
